import { zafClient } from '@/services/zendesk'
import type { VtexOrder, ZafMetadata, PatrusResponse } from '@/interfaces'

const PATRUS_BASE_URL = 'https://api-patrus.azure-api.net'
const PATRUS_PROXY_URL = 'https://proxyintegration.bcrcx.com'

interface ZafCompleteResponse {
  status: number
  responseText: string
  responseJSON: unknown
}

export class PatrusUnauthorizedError extends Error {
  constructor() { super('Token Patrus expirado (401)') }
}

export async function fetchPatrusToken(): Promise<string> {
  if (!zafClient) throw new Error('ZAF client not available')

  const meta = await zafClient.metadata() as unknown as ZafMetadata
  const { 'patrus-user': user, 'patrus-password': password, 'patrus-api-token': apiToken, 'patrus-subscription': subscription, 'patrus-proxy': proxyUrl } = meta.settings

  const res = await fetch(`${proxyUrl ?? PATRUS_PROXY_URL}/vulcabras_patrus/get_token`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiToken}`,
      Subscription: subscription ?? '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username: user, password, grant_type: 'password' }),
  })

  const data = await res.json() as { access_token?: string }
  if (!data?.access_token) throw new Error('Token Patrus não retornado pelo proxy')

  return data.access_token
}

export async function getPatrusTracking(order: VtexOrder, token: string): Promise<PatrusResponse> {
  if (!zafClient) throw new Error('ZAF client not available')

  const invoiceNumber = order.packageAttachment.packages[0]?.invoiceNumber
  if (!invoiceNumber) throw new Error('Número da nota fiscal não encontrado no pedido')

  const meta = await zafClient.metadata() as unknown as ZafMetadata
  const subscription = meta.settings['patrus-subscription'] ?? ''

  let raw: ZafCompleteResponse
  try {
    raw = await zafClient.request({
      url: `${PATRUS_BASE_URL}/api/v1/logistica/crm/trackings?filtros.notaFiscal=${invoiceNumber}`,
      type: 'GET',
      httpCompleteResponse: true,
      headers: {
        Authorization: `Bearer ${token}`,
        Subscription: subscription,
      },
    }) as ZafCompleteResponse
  } catch (err: unknown) {
    // ZAF can reject instead of resolve on 4xx even with httpCompleteResponse: true
    if ((err as ZafCompleteResponse)?.status === 401) throw new PatrusUnauthorizedError()
    throw err
  }

  if (raw.status === 401) throw new PatrusUnauthorizedError()

  const response = (raw?.responseJSON as PatrusResponse)
    ?? (raw?.responseText ? JSON.parse(raw.responseText) as PatrusResponse : null)

  if (!response) throw new Error('Resposta de tracking Patrus vazia')

  return response
}
